<template>
  <button
    v-bind="btnAttrs"
    v-on="$listeners"
  >
    <slot v-if="$slots.default" />
    <template v-else>
      {{ btnContent }}
    </template>
  </button>
</template>
<script>
import lodash from 'lodash'

/**
 * Button for HKLand
 *
 * @props {string} [color='none']
 * The param can use
 * `none`, `darkblue`, `orange`, `white`, `grey`, `round`, `transparent`, `bluegray`
 * @props {string} [action]
 * @props {boolean} [block=false]
 *
 * @example
 * ```html
 * <HKLButton />
 * => <button type="button" class="btn" role="button"></button>
 * ```
 * @example
 * ```html
 * <HKLButton action="add" />
 * => <button type="button" class="btn" role="add-button">Add</button>
 * => <button type="button" class="btn" role="add-button">添加</button>
 * ```
 * @example
 * ```html
 * <HKLButton color="white" />
 * => <button type="button" class="btn btn--white" role="button"></button>
 * ```
 * @example
 * ```html
 * <HKLButton>Test Button</HKLButton>
 * => <button type="button" class="btn" role="button">Test Button</button>
 * ```
 * @example
 * ```html
 * <HKLButton :block="true">Block Button</HKLButton>
 * => <button type="button" class="btn btn--block" role="button">Block Button</button>
 * ```
 */
const HKLButton = {
  name: 'HKLButton',
  props: {
    color: {
      type: String,
      default: 'none',
      validator: (val) => {
        const vals = ['none', 'darkblue', 'orange', 'white', 'grey', 'round', 'transparent', 'bluegray', 'disabledgrey']
        return vals.includes(val)
      },
    },
    action: {
      type: String,
      default: '',
    },
    block: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    btnAttrs () {
      const classname = ['hkl-button', 'btn']
      if (this.color !== 'none') {
        classname.push(`btn--${this.color}`)
      }
      if (this.block) {
        classname.push('btn--block')
      }
      return {
        role: this.btnRole,
        ...this.$attrs,
        type: 'button',
        class: classname.join(' '),
        disabled: this.disabled,
      }
    },
    btnContent () {
      if (!this.action) return this.action
      return this.$t(`action.${this.action}`)
    },
    btnRole () {
      if (!this.action) return 'button'
      return `${lodash.kebabCase(this.action)}-button`
    },
  },
}

export default HKLButton
</script>
<style lang='scss' scoped>
.btn {
  min-width: 80px;
  min-height: 40px;
  font-family: Verdana;
  &.btn--block {
    width: 100%;
  }
  &.no-min-width {
    min-width: auto;
  }
  &.no-min-height {
    min-height: auto;
  }
}
</style>
